import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Observable, catchError, map, of, startWith, switchMap } from 'rxjs';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';

import { KickstartService } from '../../../../core/kickstart/kickstart.service';
import {
  BusinessAiConsentService,
  BusinessConsentResponse,
} from '../../../../core/attestations/business-ai-consent.service';
import {
  KsAccountConfirmationAnswer,
  KsApplicationDetail,
  KsClientContextEntry,
  KsDocumentType,
  KsNamedAccount,
} from '../../../../core/kickstart/kickstart.types';
import { IconComponent } from '../../../../shared/icon/icon';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';
import { DocDropType } from '../../shared/doc-drop/doc-drop';
import { DocUploadPanelComponent, PanelDoc } from '../../shared/doc-upload-panel/doc-upload-panel';
import { KsContextBlockComponent } from '../context-block/ks-context-block';
import { readError } from '../../../../core/http/read-error';

type Step = 'consent' | 'details' | 'documents' | 'review';

const KS_MIN = 50_000;
const KS_MAX = 1_500_000;

/** Quick-pick amounts inside the KS range — the most common ask sizes. */
const AMOUNT_PRESETS = [100_000, 250_000, 500_000, 1_000_000];

/**
 * The route's months of bank statements (`KS-OB-02a/b`): 6 with financial
 * statements, 12 without. The screen STATES the figure and checks nothing —
 * fewer months never blocks submit (DOC-72 UI-DA-72-05).
 */
export function monthsFor(hasFinancials: boolean | null): 6 | 12 | null {
  if (hasFinancials === null) return null;
  return hasFinancials ? 6 : 12;
}

/**
 * Where a resumed draft opens (FLOW-394's `?draft=`). "Nothing re-asked" holds
 * only once Details were answered: the form's financials question is required,
 * so a draft that passed through Details carries a boolean. A draft minted
 * without the client — by Ops's *Proceed* or an indicative-sizing request
 * (`applications/services.py` `_create_ks_draft`) — carries the R50k
 * placeholder and a null answer, and opens on Details so the placeholder never
 * stands as the client's own figure. The amount is never pre-filled from the
 * pre-assessment (`§A12` P1): on this path the field opens empty.
 */
export function resumeStep(
  app: Pick<KsApplicationDetail, 'has_financials_declared'>,
): 'details' | 'documents' {
  return app.has_financials_declared === null ? 'details' : 'documents';
}

/** A named account row as typed — becomes a `KsNamedAccount` when saved. */
export interface AccountRow {
  bank_name: string;
  last_four: string;
  reason: string;
}

const LAST_FOUR = /^\d{4}$/;

/** The form's own row check — the business's account step reuses it (FLOW-366). */
export function rowValid(r: AccountRow): boolean {
  return !!r.bank_name.trim() && !!r.reason.trim() && (!r.last_four || LAST_FOUR.test(r.last_four));
}

@Component({
  selector: 'app-ks-create',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatTooltipModule,
    RouterLink,
    IconComponent,
    ZarPipe,
    DocUploadPanelComponent,
    KsContextBlockComponent,
  ],
  templateUrl: './ks-create.html',
  styleUrl: './ks-create.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KsCreateComponent {
  private readonly kickstartService = inject(KickstartService);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly businessConsent = inject(BusinessAiConsentService);

  protected readonly entityId = this.route.snapshot.paramMap.get('id') ?? '';
  protected readonly AMOUNT_PRESETS = AMOUNT_PRESETS;
  protected readonly KS_MIN = KS_MIN;
  protected readonly KS_MAX = KS_MAX;

  protected readonly step = signal<Step>('details');
  protected readonly draft = signal<KsApplicationDetail | null>(null);

  // --- Documents: the shared upload panel owns upload → verdict poll → remove ---

  /** Documents already on the draft — the panel seeds itself from these once. */
  protected readonly initialDocs = computed<PanelDoc<KsDocumentType>[]>(
    () => this.draft()?.documents ?? [],
  );
  /** Settled (server-stored) documents, fed by the panel — what actually gets submitted. */
  protected readonly attachedDocs = signal<PanelDoc<KsDocumentType>[]>([]);
  /**
   * Files that never reached the server. They don't block submitting — but the
   * review names them, so nobody submits believing a file is attached when it
   * isn't (feedback j2NXBFQ3).
   */
  protected readonly failedUploads = signal<string[]>([]);
  /** True while the panel has an upload in flight (blocks "Review & submit"). */
  protected readonly anyInFlight = signal(false);

  // Backend calls the panel needs, as stable arrow fields so its function
  // inputs don't change identity between renders.
  protected readonly uploadDoc = (shortId: string, file: File, docType: KsDocumentType) =>
    this.kickstartService.uploadDocument(shortId, file, docType);
  protected readonly deleteDoc = (shortId: string, docShortId: string) =>
    this.kickstartService.deleteDocument(shortId, docShortId);
  protected readonly fetchDocs = (shortId: string) =>
    this.kickstartService.get(shortId).pipe(map((app) => app.documents ?? []));

  protected readonly isSaving = signal(false);
  protected readonly isSubmitting = signal(false);
  protected readonly serverError = signal<string | null>(null);

  // --- Step 0: the business's AI-assessment consent (DOC-72 UI-DA-72-01) ----
  // The consent is the BUSINESS's (`FC-DET-055`) and is given by its own act —
  // never by Continue or Submit. `undefined` = still reading it.

  protected readonly consent = signal<BusinessConsentResponse | undefined>(undefined);
  protected readonly consentOnFile = computed(() => this.consent()?.ai_consent?.state === 'ACTIVE');
  /** Set once at load: the step stays in the stepper after the answer is given. */
  protected readonly consentAsked = signal(false);
  protected readonly consentAnswer = signal<'YES' | 'NO' | null>(null);
  protected readonly isGivingConsent = signal(false);
  /** `FC-DET-051`: the upload is blocked while the consent is absent or withdrawn. */
  protected readonly uploadsBlocked = computed(() => !this.consentOnFile());

  // --- Step 1: details -------------------------------------------------------

  protected readonly form = this.fb.group({
    amount: this.fb.control<number | null>(null, [
      Validators.required,
      Validators.min(KS_MIN),
      Validators.max(KS_MAX),
    ]),
    purpose: this.fb.control<string>('', [Validators.maxLength(2000)]),
    // DOC-72 UI-DA-72-02: no answer pre-selected, and an answer is needed to go on.
    hasFinancials: this.fb.control<boolean | null>(null, [Validators.required]),
  });

  private readonly hasFinancials = toSignal(
    this.form.controls.hasFinancials.valueChanges.pipe(
      startWith(this.form.controls.hasFinancials.value),
    ),
    { initialValue: null },
  );
  protected readonly months = computed(() => monthsFor(this.hasFinancials() ?? null));

  /** The upload blocks: financial statements only on a *Yes* (UI-DA-72-03). */
  protected readonly docTypes = computed<DocDropType<KsDocumentType>[]>(() => {
    const months = this.months() ?? 6;
    const blocks: DocDropType<KsDocumentType>[] = [
      {
        value: 'BANK_STATEMENT',
        label: 'Bank statements',
        hint: `Every business bank account — the last ${months} months each`,
      },
    ];
    if (this.hasFinancials() === true) {
      blocks.push({
        value: 'FINANCIALS',
        label: 'Financial statements',
        hint: 'Annual or management accounts',
      });
    }
    blocks.push({
      value: 'OTHER',
      label: 'Other supporting documents',
      hint: 'Anything else relevant',
    });
    return blocks;
  });

  protected readonly hasBankStatement = computed(() =>
    this.attachedDocs().some((d) => d.doc_type === 'BANK_STATEMENT'),
  );

  /**
   * The route as the system will set it at submit — with financial statements
   * only where one is on file (`eligibility.evaluate_front_door`). A *Yes* at
   * step 1 with none uploaded is the bank-only route, and never blocks.
   */
  protected readonly routeLabel = computed(() =>
    this.attachedDocs().some((d) => d.doc_type === 'FINANCIALS')
      ? 'With financial statements'
      : 'Bank statements only',
  );

  // --- Step 2: the every-account confirmation (DOC-72 UI-DA-72-04) ----------
  // Required. Saved on Submit, through its own endpoint, which writes the
  // declarations; no wording here speaks of honesty or disclosure (`FC-DET-047`).

  protected readonly accountsAnswer = signal<KsAccountConfirmationAnswer | null>(null);
  protected readonly accountRows = signal<AccountRow[]>([]);
  /** The answer as last saved — re-saving an unchanged answer appends nothing. */
  private savedConfirmation: string | null = null;

  protected readonly confirmationValid = computed(() => {
    const answer = this.accountsAnswer();
    if (answer === 'ALL_UPLOADED') return true;
    if (answer !== 'NOT_ALL') return false;
    const rows = this.accountRows();
    return rows.length > 0 && rows.every(rowValid);
  });

  // --- Below the uploads: *Tell us about your business* (UI-DA-72-08) -------
  // Optional; never blocks. Client context — never evidence: the back end
  // records it with its provenance and no assessment reads it.

  private readonly contextBlock = viewChild(KsContextBlockComponent);
  /** What the block holds, for the review row; the block itself does the saving. */
  protected readonly contextEntries = signal<KsClientContextEntry[]>([]);
  protected readonly contextBusy = signal(false);

  /** Step order — the consent step only where it was needed at load. */
  protected readonly steps = computed<{ id: Step; label: string }[]>(() => [
    ...(this.consentAsked() ? [{ id: 'consent' as Step, label: 'Consent' }] : []),
    { id: 'details', label: 'Details' },
    { id: 'documents', label: 'Documents' },
    { id: 'review', label: 'Review' },
  ]);

  protected readonly currentIndex = computed(() =>
    this.steps().findIndex((s) => s.id === this.step()),
  );

  /** What still stops Submit, in DOC-72 §5's words. */
  protected readonly missing = computed(() => {
    const lines: string[] = [];
    if (!this.consentOnFile()) {
      lines.push("Give the business's consent to AI assessment to submit.");
    }
    if (!this.hasBankStatement()) lines.push('Add your bank statements to submit.');
    if (!this.confirmationValid()) {
      lines.push('Answer the question about your bank accounts to submit.');
    }
    return lines;
  });

  constructor() {
    const resumeId = this.route.snapshot.queryParamMap?.get('draft') ?? null;
    this.businessConsent
      .getState(this.entityId)
      .pipe(
        // A failed read falls back to asking; the server decides either way.
        catchError(() => of({ ai_consent: null } as BusinessConsentResponse)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        this.consent.set(res);
        if (res.ai_consent?.state !== 'ACTIVE') {
          this.consentAsked.set(true);
          this.step.set('consent');
        } else if (resumeId) {
          // The draft may still be loading — resume() places the step once it has.
          const draft = this.draft();
          if (draft) this.step.set(resumeStep(draft));
        }
        this.cdr.markForCheck();
      });
    if (resumeId) this.resume(resumeId);
  }

  /** After step 0: a resumed draft opens where `resumeStep` says; a fresh start on Details. */
  private stepAfterConsent(): Step {
    const draft = this.draft();
    return draft ? resumeStep(draft) : 'details';
  }

  protected stepState(id: Step): 'done' | 'active' | 'upcoming' {
    const i = this.steps().findIndex((s) => s.id === id);
    const current = this.currentIndex();
    if (i < current) return 'done';
    if (i === current) return 'active';
    return 'upcoming';
  }

  /** Continue from step 0: *Yes* gives the consent by its own act; *No* goes on, uploads blocked. */
  protected continueFromConsent(): void {
    const answer = this.consentAnswer();
    if (answer === null || this.isGivingConsent()) return;
    if (answer === 'NO' || this.consentOnFile()) {
      this.step.set(this.stepAfterConsent());
      return;
    }
    this.isGivingConsent.set(true);
    this.serverError.set(null);
    this.businessConsent
      .give(this.entityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.consent.set(res);
          this.isGivingConsent.set(false);
          this.step.set(this.stepAfterConsent());
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isGivingConsent.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  protected createDraft(): void {
    if (this.form.invalid || this.isSaving()) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSaving.set(true);
    this.serverError.set(null);

    const v = this.form.getRawValue();
    const fields = {
      amount_requested: String(v.amount),
      purpose: v.purpose?.trim() || undefined,
      has_financials_declared: v.hasFinancials,
    };
    // A draft already exists (stepped back to Details, or resumed) — update
    // it rather than create a second one.
    const existing = this.draft();
    const call$: Observable<KsApplicationDetail> = existing
      ? this.kickstartService.capture(existing.short_id, { ...fields, purpose: v.purpose ?? '' })
      : this.kickstartService.create({ entity: this.entityId, ...fields });
    call$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (draft) => {
        this.draft.set(draft);
        this.step.set('documents');
        this.isSaving.set(false);
        this.cdr.markForCheck();
      },
      error: (err: unknown) => {
        this.isSaving.set(false);
        this.serverError.set(readError(err));
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Pick a resumed draft back up — what it already holds is shown, and only
   * what was never answered is asked (`resumeStep`). A draft whose Details
   * were never confirmed opens there with the amount EMPTY: the figure on the
   * record is proceed's placeholder, not the client's ask.
   */
  private resume(shortId: string): void {
    this.kickstartService
      .get(shortId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (app) => {
          if (app.application_stage !== 'DRAFT') {
            void this.router.navigate(['/entity', this.entityId, 'kickstart', app.short_id]);
            return;
          }
          this.draft.set(app);
          const detailsConfirmed = resumeStep(app) === 'documents';
          this.form.patchValue({
            amount: detailsConfirmed ? Number(app.amount_requested) : null,
            purpose: app.purpose ?? '',
            hasFinancials: app.has_financials_declared,
          });
          // Unless the business's consent question is up — continueFromConsent()
          // then places the step by the same rule.
          if (this.step() !== 'consent') this.step.set(resumeStep(app));
          // The upload panel seeds itself from initialDocs() once the draft is set.
          this.contextEntries.set(app.client_context ?? []);
          const confirmation = app.account_confirmation;
          if (confirmation) {
            this.accountsAnswer.set(confirmation.answer);
            this.accountRows.set(confirmation.named_accounts.map((a) => ({ ...a })));
            this.savedConfirmation = this.confirmationKey();
          }
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  protected pickAmount(amount: number): void {
    this.form.controls.amount.setValue(amount);
    this.form.controls.amount.markAsDirty();
  }

  protected goToStep(id: Step): void {
    // Only allow jumping back to a completed step, never skipping ahead.
    if (this.stepState(id) === 'done') {
      this.step.set(id);
    }
  }

  protected back(): void {
    const prev = this.steps()[this.currentIndex() - 1];
    if (prev) {
      this.step.set(prev.id);
    }
  }

  // --- The account confirmation ---------------------------------------------

  protected setAccountsAnswer(answer: KsAccountConfirmationAnswer): void {
    this.accountsAnswer.set(answer);
    if (answer === 'NOT_ALL' && this.accountRows().length === 0) this.addAccountRow();
  }

  protected addAccountRow(): void {
    this.accountRows.update((rows) => [...rows, { bank_name: '', last_four: '', reason: '' }]);
  }

  protected removeAccountRow(index: number): void {
    this.accountRows.update((rows) => rows.filter((_, i) => i !== index));
  }

  protected updateAccountRow(index: number, field: keyof AccountRow, value: string): void {
    this.accountRows.update((rows) =>
      rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    );
  }

  protected rowInvalid(row: AccountRow): boolean {
    return !rowValid(row);
  }

  private confirmationKey(): string {
    const answer = this.accountsAnswer();
    const rows = answer === 'NOT_ALL' ? this.accountRows() : [];
    return JSON.stringify({ answer, rows });
  }

  private saveConfirmation$(shortId: string): Observable<unknown> {
    const key = this.confirmationKey();
    if (key === this.savedConfirmation) return of(null);
    const answer = this.accountsAnswer()!;
    const rows: KsNamedAccount[] =
      answer === 'NOT_ALL'
        ? this.accountRows().map((r) => ({
            bank_name: r.bank_name.trim(),
            last_four: r.last_four.trim(),
            reason: r.reason.trim(),
          }))
        : [];
    return this.kickstartService.confirmAccounts(shortId, answer, rows).pipe(
      switchMap((saved) => {
        this.savedConfirmation = key;
        return of(saved);
      }),
    );
  }

  protected addANote(): void {
    this.step.set('documents');
  }

  /** On to review — a note typed but not yet added is kept as an entry first. */
  protected toReview(): void {
    const block = this.contextBlock();
    if (block?.hasPendingText()) {
      // Moves on once the entry is saved; a refusal keeps the step, with why.
      block.addNote(() => this.step.set('review'));
      return;
    }
    this.step.set('review');
  }

  /**
   * Submit (UI-DA-72-05): only with a bank statement, the confirmation answered
   * and the consent on file. The confirmation is saved first, by its own act;
   * the submit body carries nothing — it records no consent (UI-DA-72-06).
   */
  protected submit(): void {
    const draft = this.draft();
    if (!draft || this.isSubmitting() || this.missing().length > 0) {
      return;
    }
    this.isSubmitting.set(true);
    this.serverError.set(null);

    this.saveConfirmation$(draft.short_id)
      .pipe(
        switchMap(() => this.kickstartService.submit(draft.short_id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.snackBar.open(res.message, 'Dismiss', { duration: 6000 });
          void this.router.navigate(['/entity', this.entityId, 'kickstart', draft.short_id]);
        },
        error: (err: unknown) => {
          this.isSubmitting.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  /** " by [name, capacity]" after "given [date]" — the capacity in plain words (`FC-DET-065`). */
  protected readonly givenBy = computed(() => {
    const name = this.consent()?.given_by_name;
    if (!name) return '';
    const givenAs = this.consent()?.ai_consent?.given_as;
    const capacity =
      givenAs === 'AGENT'
        ? 'the introducing agent, for the business'
        : givenAs === 'MEMBER'
          ? 'a member of the business'
          : '';
    return capacity ? ` by ${name}, ${capacity}` : ` by ${name}`;
  });
}
